/**
 * Login Page - B2C authentication with modern design
 */
import { Link } from 'react-router-dom'
import { Globe, ArrowLeft, Shield, Sparkles, Clock } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Separator } from '../components/ui/separator'
import { useAuth } from '@mzanzi/auth'
import { ThemeToggle } from '../components/theme-toggle'

const benefits = [
  { icon: Shield, text: 'Enterprise-grade security' },
  { icon: Sparkles, text: 'AI-powered automation' },
  { icon: Clock, text: '24/7 local support' },
]

function BrandPanel() {
  return (
    <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary via-primary to-blue-600 p-12 flex-col justify-between relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl" />
      </div>

      <div className="relative z-10">
        <Link to="/" className="flex items-center gap-3 text-white">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/20 backdrop-blur">
            <Globe className="h-6 w-6" />
          </div>
          <span className="text-2xl font-bold">Mzanzi Cloud</span>
        </Link>
      </div>

      <div className="relative z-10 space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-white mb-4">
            Cloud Services for Africa&apos;s Digital Future
          </h1>
          <p className="text-lg text-white/80">
            Join thousands of MSPs managing their cloud services
            with the platform built for Africa.
          </p>
        </div>
        <div className="space-y-3">
          {benefits.map((benefit) => (
            <div key={benefit.text} className="flex items-center gap-3 text-white/90">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
                <benefit.icon className="h-4 w-4" />
              </div>
              {benefit.text}
            </div>
          ))}
        </div>
      </div>

      <div className="relative z-10 text-white/60 text-sm">
        © {new Date().getFullYear()} Mzanzi Cloud. All rights reserved.
      </div>
    </div>
  )
}

function MicrosoftIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none">
      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
    </svg>
  )
}

function LoginButton({ isLoading, onClick }: { isLoading: boolean; onClick: () => void }) {
  return (
    <Button onClick={onClick} disabled={isLoading} className="w-full h-12 text-base gap-3" size="lg">
      {isLoading ? (
        <>
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          Connecting...
        </>
      ) : (
        <>
          <MicrosoftIcon />
          Sign in with Microsoft
        </>
      )}
    </Button>
  )
}

function LoginCard() {
  const { login, isLoading } = useAuth()

  const handleLogin = async () => {
    try {
      await login()
    } catch {
      // MSAL handles error display via redirect
    }
  }

  return (
    <Card className="border-0 shadow-lg sm:border">
      <CardHeader className="text-center pb-4">
        <CardTitle className="text-2xl">Welcome back</CardTitle>
        <CardDescription>Sign in to your Mzanzi Cloud account</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <LoginButton isLoading={isLoading} onClick={handleLogin} />

        <div className="relative">
          <div className="absolute inset-0 flex items-center"><Separator /></div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">Secure authentication</span>
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground">
          By signing in, you agree to our{' '}
          <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
        </p>
      </CardContent>
    </Card>
  )
}

function LoginHeader() {
  return (
    <div className="flex items-center justify-between p-4 lg:p-6">
      <Link to="/" className="flex items-center gap-2 lg:invisible">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-blue-600">
          <Globe className="h-5 w-5 text-white" />
        </div>
        <span className="text-xl font-bold">Mzanzi</span>
      </Link>
      <div className="flex items-center gap-2">
        <ThemeToggle />
        <Button variant="ghost" size="sm" asChild className="hidden sm:inline-flex">
          <Link to="/" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
        </Button>
      </div>
    </div>
  )
}

export function LoginPage() {
  return (
    <div className="min-h-screen flex">
      <BrandPanel />

      <div className="flex-1 flex flex-col">
        <LoginHeader />

        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
          <div className="w-full max-w-md">
            <LoginCard />
            <p className="mt-6 text-center text-sm text-muted-foreground">
              Don&apos;t have an account?{' '}
              <Link to="/#pricing" className="text-primary hover:underline font-medium">
                Get started for free
              </Link>
            </p>
          </div>
        </div>

        <div className="p-4 text-center text-sm text-muted-foreground lg:hidden">
          © {new Date().getFullYear()} Mzanzi Cloud
        </div>
      </div>
    </div>
  )
}
